import { BoltIcon, CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import Button from '../../components/Button'
import { useLocation, useNavigate } from 'react-router-dom'
import twitterLogo from '../../assets/twitter-62x62.png'
import facebookLogo from '../../assets/facebook-62x62.png'
import youtubeLogo from '../../assets/youtube_logo_62x43.png'
import instagramLogo from '../../assets/instagram-62x62.png'
import tiktokLogo from '../../assets/tiktok_logo_65x65.jpg'

function IntroStepPage() {
    const navigate = useNavigate()
    const location = useLocation()

    const protectionTypes = [
        { id: 'abuse', label: 'Abuse & harassment', icon: ShieldCheckIcon },
        { id: 'threats', label: 'Threats & violence', icon: ShieldCheckIcon },
        { id: 'spam', label: 'Spam & bot accounts', icon: ShieldCheckIcon }
    ]

    const platforms = [
        {
            title: 'Twitter/X profile',
            icon: twitterLogo,
            bgColor: 'bg-sky-500/10'
        },
        {
            title: 'Facebook page',
            icon: facebookLogo,
            bgColor: 'bg-blue-500/10'
        },
        {
            title: 'YouTube channel',
            icon: youtubeLogo,
            bgColor: 'bg-red-500/10'
        },
        {
            title: 'Instagram profile',
            icon: instagramLogo,
            bgColor: 'bg-pink-500/10'
        },
        {
            title: 'TikTok',
            to: '/account/new/tiktok_ad',
            icon: tiktokLogo,
            bgColor: 'bg-cyan-500/10'
        }
    ]

    return (
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-border shadow-lg max-w-xl mx-auto animate-fade-in">
            <div className="mx-auto h-14 w-14 rounded-full bg-red-50 mb-4 flex items-center justify-center">
                <ShieldCheckIcon className="w-8 h-8 text-red-500" />
            </div>
            <div className="mb-6 text-center">
                <h1 className="text-2xl md:text-3xl mb-3">
                    You're moments away from safer social media
                </h1>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-4">
                    Your organisation has teamed up with Arwen, who use proven AI to protect elite
                    athletes globally from toxic and threatening comments on social media. This
                    service is provided free to you, as it's fully funded by your organisation
                </p>

                <div className="flex items-center justify-center gap-5 my-6">
                    {platforms.map(({ icon, title }) => {
                        return (
                            <img
                                key={title}
                                src={icon}
                                alt={title}
                                className="w-8 h-8 object-contain rounded-md"
                            />
                        )
                    })}
                </div>
            </div>

            <div className="mb-6">
                <h3 className="text-base font-semibold text-foreground mb-3">
                    You'll be protected from:
                </h3>
                <div className="grid gap-2">
                    {protectionTypes.map((protection) => {
                        return (
                            <div
                                key={protection.id}
                                className="p-3 rounded-lg border-lime-400  border bg-lime-400/5"
                            >
                                <div className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5 text-lime-500 flex-shrink-0" />
                                    <span className="text-sm font-medium text-foreground">
                                        {protection.label}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="mb-6">
                <h3 className="text-base font-semibold text-foreground mb-3">How Arwen works</h3>

                <div className="grid md:grid-cols-2 gap-3">
                    <div className="p-3 bg-red-400/5 border border-red-400 rounded-lg">
                        <BoltIcon className="w-6 h-6 text-red-500 mb-2" />
                        <h4 className="font-semibold text-sm text-foreground mb-1 mt-0">
                            Moderate
                        </h4>
                        <p className="text-xs text-muted-foreground">
                            Instantly and discreetly hides toxic and spam comments across 30+
                            languages (commenter is not informed).
                        </p>
                    </div>

                    <div className="p-3 bg-red-400/5 border border-red-400 rounded-lg">
                        <ShieldCheckIcon className="w-6 h-6 text-red-500 mb-2" />
                        <h4 className="font-semibold text-sm text-foreground mb-1 mt-0">Protect</h4>
                        <p className="text-xs text-muted-foreground">
                            Detects threatening comments and triggers an investigation to make sure
                            you're safe.
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/50 rounded-lg mb-6">
                <h4 className="font-semibold text-sm text-foreground mb-2 flex items-center gap-2 mt-0">
                    <ShieldCheckIcon className="w-5 h-5 text-primary" />
                    You're in safe hands
                </h4>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">✓</span>
                        <span>
                            Arwen is an official partner to the social networks - when you authorise
                            us you are protected by secure OAuth2 protocols
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">✓</span>
                        <span>We never post and we never see your Direct Messages</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">✓</span>
                        <span>You control everything and can remove access any time</span>
                    </li>
                </ul>
            </div>

            <div className="space-y-4">
                <Button
                    onClick={() => navigate(`/authenticate${location.search}`)}
                    size="large"
                    text="Create my account"
                    primary
                    full
                />
                {/* TODO: pass param so box can be set to sign in and progress bar displayed */}
                <Button
                    onClick={() =>
                        navigate(`/authenticate${location.search}`, {
                            state: { signIn: true }
                        })
                    }
                    size="large"
                    text="Sign in"
                    primary
                    full
                />
            </div>
        </div>
    )
}

export default IntroStepPage
